/*
 * Profile flow — V1.9.1 (design handoff README §The flows → B. Profile).
 * Five steps: name → date of birth + gender → nationality & residence →
 * emergency contact → preferences, in the SHARED flow chrome (header with
 * back chevron + context + `Step N of M` + 5px progress bar; scrolling body;
 * footer with the amber blocked-reason bar directly above the primary button).
 *
 * TWO SUBJECTS, one flow (README §Flows B): the account holder (self), or a
 * MANAGED CHILD (`/profile/children/:childKey/step-1..5`). Step 2+ shows the
 * subject's name in the header so a parent filling in for several children
 * always knows which one. The inner component is KEYED by subject, so every
 * in-flight answer is stamped with its subject and RESET on open — a stale
 * answer can never apply to another person (README §State draft scoping).
 *
 * PERSISTENCE:
 *  - self → the EXISTING my-api/Cognito profile path (profileStore.ts
 *    loadSelfDraft/saveSelfDraft) — real attributes + the device-local extras
 *    seam. Nothing new is invented here.
 *  - child → the P2 child store (assignStore `child_{key}` records). my-api
 *    has NO child-profile contract yet, so these records stay client-seamed
 *    (// TODO WIRE in assignStore.ts) and are never presented as server
 *    writes.
 *
 * Completion is DERIVED from the data (wizardModel.allValid), never a stored
 * flag. Partial save reads `Progress saved` + `Still needed: …` + `Add these
 * now`; complete reads `Profile complete` — and the receipt header shows
 * `SAVED`, not `Step 5 of 5`.
 *
 * NOTE: no age-band gate here — from Profile there is no ticket context, and a
 * child with no ticket gets `Account profile` + NO age gate (QA 8.7). Band
 * checks live in the assign flow, where the ticket context exists.
 *
 * HYDRATION HOLD (CMT-2026-09-03-026, Duncan's blank-stepper defect): the
 * SELF subject's draft loads ASYNC from the saved Cognito profile + extras
 * (loadSelfDraft) — but the step body used to render its EMPTY inputs
 * immediately, so an account WITH a saved profile always saw a blank step
 * first (and, on a failed read, forever). The body + footer are now HELD
 * (the configPending pattern) until the mount-time draft load settles: a
 * profiled account NEVER sees blank fields. The child flow seeds
 * synchronously and is never held.
 *
 * HUB ENTRY (`?hub=1`, CMT-2026-09-03-026): a deep link from the account
 * edit hub (/profile/edit) into ONE step. The footer's primary action is
 * SAVE on every step (never Continue-through-the-flow), and save/back/exit
 * all return to the HUB — a single-field edit is one step in, one save out.
 * Absent ⇒ today's flow, byte-identical. Composes with the guardian entry
 * untouched (get-ready never sets hub=1).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useShellChrome } from '../../components/Shell'
import { useLocale } from '../../i18n/useLocale'
import {
  cityKnown, cityMatches, dialOptions, nationalityMatches, residenceMatches, countryByIso,
  type RefCountry,
} from './referenceData'
import {
  ageFromDob, allValid, blockText, CHILD_YEARS, DAYS, DEFAULT_DIAL, dobFull, dobIso, EMPTY_DRAFT,
  filterPhoneInput, firstIncompleteStep, GENDER_MODES, JUNIOR_SHIRT_SIZES, LANGUAGES,
  missingList, MONTHS, phoneState, RELATIONSHIPS, SELF_DESCRIBE_PILL, SELF_SHIRT_SIZES,
  SELF_YEARS, stepNeeds, suggestedJuniorShirt, summaryRows, withIdentityFrom, type WizardDraft,
} from './wizardModel'
import { DEFAULT_WIZARD_CONFIG, resolveWizardConfig, type WizardConfig } from './wizardConfig'
import { SummaryCard } from './SummaryCard'
import {
  fetchParticipantConfigDataCollection, participantConfigApiBase,
} from '../../data/participantConfig'
import { loadSession } from '../../auth/session'
import { ensureLiveSession, redirectToSignIn } from '../../auth/sessionExpiry'
import {
  loadIdentityVerificationLevel, loadLegacyDraft, loadSelfDraft, saveSelfDraft,
} from './profileStore'
import type { IdentityVerificationLevel } from '../../data/myApi'
import {
  declaredChildRecordApplies, getChildProfile, listChildren, saveChildProfile, type ChildRecord,
} from '../orders/assignStore'
import {
  fetchHeldEligibilityContracts, onProfileSaved, type HeldEligibilityContract,
} from '../../data/getReady'
import { guardianRecordApplies } from '../../data/guardianClaim'
import { takeLegacyPrefillPending } from '../../data/myApi'
import { eligibilityGate, eligibilityMismatchCopy, eligibilityRejectCopy } from '../../data/eligibility'
import { EligibilityMismatchNotice } from '../../components/EligibilityMismatchNotice'
import { useDataOptional } from '../../data/DataProvider'

// ---------------------------------------------------------------------------
// Styles (V1.9.1 tokens; DC inline styles transcribed)
// ---------------------------------------------------------------------------

const inputStyle: CSSProperties = {
  width: '100%', minHeight: 46, border: '1px solid var(--hairline-14)',
  borderRadius: 11, padding: '0 13px', fontSize: 15, background: 'var(--bg-frame)',
  color: 'var(--text)', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
}
const selectStyle: CSSProperties = {
  ...inputStyle, padding: '0 8px', appearance: 'none', WebkitAppearance: 'none',
}
const labelStyle: CSSProperties = {
  fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 6,
}
// Selected pill: accent bg + accent-ink; unselected: input bg + hairline .14 +
// softbtn (canon §5 token criteria).
const pillStyle = (selected: boolean): CSSProperties => ({
  borderRadius: 20, padding: '0 15px', minHeight: 44, fontFamily: 'inherit',
  fontSize: 13, fontWeight: 700, cursor: 'pointer',
  ...(selected
    ? { background: 'var(--accent)', color: 'var(--accent-ink)', border: 0 }
    : { background: 'var(--bg-frame)', color: 'var(--text-softbtn)', border: '1px solid var(--hairline-14)' }),
})
const helpBtnStyle: CSSProperties = {
  width: 22, height: 22, flex: 'none', borderRadius: '50%',
  border: '1px solid rgba(255,255,255,.2)', background: 'transparent', color: 'var(--text-muted)',
  fontFamily: 'inherit', fontSize: 11, fontWeight: 800, cursor: 'pointer',
  display: 'grid', placeItems: 'center', padding: 0,
}
const helpBoxStyle: CSSProperties = {
  fontSize: 11.5, lineHeight: 1.5, color: 'var(--text-secondary)', background: 'var(--bg-frame)',
  border: '1px solid var(--hairline-10)', borderRadius: 10, padding: '10px 12px', margin: '8px 0 0',
}
const listBoxStyle: CSSProperties = {
  background: 'var(--bg-frame)', border: '1px solid var(--hairline-14)',
  borderRadius: 11, marginTop: 6, overflow: 'hidden',
}
const listRowStyle: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 9, width: '100%', border: 0,
  background: 'transparent', color: 'var(--text)', fontFamily: 'inherit', fontSize: 13.5,
  fontWeight: 600, textAlign: 'start', minHeight: 44, padding: '0 12px', cursor: 'pointer',
  borderBottom: '1px solid var(--hairline-06)',
}
const hintStyle = (ok: boolean): CSSProperties => ({
  fontSize: 11.5, marginTop: 7,
  ...(ok ? { color: 'var(--success)', fontWeight: 700 } : { color: 'var(--text-faint)' }),
})
const eyebrowStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '.1em',
  textTransform: 'uppercase', color: 'var(--text-dim)', marginBottom: 4,
}
const prefillBtnStyle: CSSProperties = {
  border: '1px solid rgba(63,224,200,.3)', borderRadius: 9, background: 'transparent',
  color: 'var(--accent-bright)', fontFamily: 'inherit', fontSize: 12, fontWeight: 700,
  cursor: 'pointer', minHeight: 44, padding: '0 12px', textAlign: 'start',
}
/** Phone input border tint: idle hairline / valid teal / invalid red (§5.3). */
const phoneBoxStyle = (tone: 'idle' | 'valid' | 'invalid'): CSSProperties => ({
  ...inputStyle,
  border: `1px solid ${tone === 'idle' ? 'var(--hairline-14)' : tone === 'valid' ? 'rgba(18,196,172,.55)' : 'var(--error-border)'}`,
})
const phoneHintStyle = (tone: 'idle' | 'valid' | 'invalid'): CSSProperties => ({
  fontSize: 11.5, marginTop: 7,
  ...(tone === 'idle'
    ? { color: 'var(--text-faint)' }
    : tone === 'valid'
      ? { color: 'var(--success)', fontWeight: 700 }
      : { color: 'var(--error)', fontWeight: 700 }),
})
// LTR island — the dial-code + phone row stays LTR inside a mirrored page
// (README §RTL).
const phoneRowStyle: CSSProperties = {
  display: 'grid', gridTemplateColumns: '104px minmax(0,1fr)', gap: 8, direction: 'ltr',
}
const noteStyle: CSSProperties = {
  fontSize: 11.5, color: 'var(--text-faint)', lineHeight: 1.45, margin: '-2px 0 9px',
}
const warnBoxStyle: CSSProperties = {
  display: 'flex', gap: 10, background: 'rgba(230,193,90,.09)',
  border: '1px solid rgba(230,193,90,.3)', borderRadius: 12, padding: '12px 13px', marginTop: 12,
}
// Identity-lock note (PROGRAMME-RULE-IDENTITY-FIELD-EDIT-LOCK-BY-VERIFICATION-
// LEVEL-V1): the calm teal informational treatment (legacy-review banner
// family), NOT the amber warn — a verified identity is a settled good state,
// not a problem to fix.
const identityLockedNoteStyle: CSSProperties = {
  fontSize: 12, lineHeight: 1.5, color: 'var(--text-secondary)',
  background: 'rgba(18,196,172,.08)', border: '1px solid rgba(18,196,172,.3)',
  borderRadius: 10, padding: '10px 12px', margin: '0 0 14px',
}
// Locked identity field: same visual dimming as the residence-locked city
// input (opacity .55) — one disabled voice across the wizard.
const lockedFieldStyle: CSSProperties = { opacity: 0.55 }

function Gap({ h }: { h: number }) {
  return <div style={{ height: h }} />
}

function TypeaheadList({ testid, children }: { testid: string; children: ReactNode }) {
  return (
    <div data-testid={testid} style={listBoxStyle}>{children}</div>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// Step ids are FIXED (1=name … 5=preferences); the VISIBLE step list is
// derived from config (CMT-2026-08-29-022) — when every preferences question
// is toggled off the step drops entirely: 4 steps, `of 4`, no gap. Keeping the
// ids stable means routes, eyebrow indexing and stepNeeds never shift.
const MAX_STEP = 5

/** Seed a child's flow draft: the saved `child_{key}` profile record when one
 *  exists, else the identity facts already on the ChildRecord (name / DOB /
 *  gender pre-set from the add-child form or the assign flow) — the user's own
 *  earlier input, never an invention. */
function childDraftFor(record: ChildRecord): WizardDraft {
  return getChildProfile(record.key) ?? {
    ...EMPTY_DRAFT,
    first: record.first, last: record.last,
    dobD: record.dobD, dobM: record.dobM, dobY: record.dobY,
    gender: record.gender,
  }
}

export function ProfileWizard({ config = DEFAULT_WIZARD_CONFIG }: { config?: WizardConfig }) {
  const { childKey } = useParams()
  const [search] = useSearchParams()
  // EVENT-SCOPED entry (CMT-2026-08-29-022): Get ready's profile step link
  // carries `?event={eventId}` so the wizard can apply that event's question
  // toggles (askLanguage/askShirt) from settings' resolved participant-config.
  // No param (account entry, child flow) ⇒ the passed/default config — both
  // questions asked, today's behaviour. The child flow is never event-scoped.
  const eventId = childKey ? null : search.get('event')
  // GUARDIAN-CLAIMED entry (S2 slice, BOARDROOM-2026-09-03-052111 /
  // CMT-2026-09-03-020): `&guardian=1` rides from a guardian seat's get-ready
  // card. The SELF wizard then presents step 1 as ACCOUNT-HOLDER data ("your
  // child's race details come next") — never the bib/results framing Duncan
  // hit live. Absent ⇒ today's copy/behaviour, byte-identical.
  const guardianEntry = !childKey && search.get('guardian') === '1'
  // ACCOUNT-HUB entry (CMT-2026-09-03-026): `?hub=1` rides from a summary-row
  // pencil on /profile/edit — save/back return to the hub, not onward through
  // the flow. Valid for BOTH subjects (the hub carries child cards too).
  // IGNORED when an `event` param is present (adversarial review F4): hub
  // entries are account-scoped by construction — a crafted
  // `?event=X&hub=1` URL must not teleport a get-ready-scoped flow out to
  // the hub on save/back.
  const hubEntry = search.get('hub') === '1' && !search.get('event')
  const [eventConfig, setEventConfig] = useState<WizardConfig | null>(null)
  // Which eventId the loaded eventConfig belongs to. The hold is DERIVED from
  // it (P2-2, adversarial review): `useState(!!eventId)` armed on the initial
  // render only, so a history traversal transitioning no-event→event (or
  // event A→B) WITHOUT a remount skipped the hold and live-switched 5→4.
  // `eventId !== loadedFor` re-arms it on every in-place eventId change.
  const [loadedFor, setLoadedFor] = useState<string | null>(null)
  // While the event config is in flight the step COUNT must stay stable (no
  // 5→4 progress flash) — the inner component holds the body until resolved.
  const configPending = !!eventId && eventId !== loadedFor

  useEffect(() => {
    if (!eventId) return
    const id = eventId // narrowed capture for the async closure
    let cancelled = false
    async function load() {
      // The config THIS load resolves to; null = defaults (ask everything).
      let next: WizardConfig | null = null
      try {
        const base = participantConfigApiBase()
        const session = loadSession()
        if (!base || !session) {
          // Seam unconfigured / signed out — fail toward asking (defaults),
          // never block the wizard on config. Honest note, no silent path.
          console.warn(`participant-config unavailable for event ${id} — asking all profile questions`)
        } else {
          const resolved = await fetchParticipantConfigDataCollection(id, base, session.idToken)
          next = resolveWizardConfig(resolved)
        }
      } catch (e) {
        // Fetch failure ⇒ defaults + warn (CMT-2026-08-29-022: config can
        // hide a question, but its absence must never block the wizard).
        console.warn(`participant-config fetch failed for event ${id} — asking all profile questions`, e)
      } finally {
        if (!cancelled) {
          // One atomic settle: the config (or defaults) + the id it belongs
          // to — a stale prior event's config can never survive a switch.
          setEventConfig(next)
          setLoadedFor(id)
        }
      }
    }
    void load()
    return () => { cancelled = true }
  }, [eventId])

  // KEYED remount per subject: every keystroke of in-flight state is stamped
  // to its subject and reset on open — a stale answer can never leak onto
  // another person (README §State: draft scoping).
  // Config resolution: an event-scoped entry uses the event's resolved config
  // once loaded (defaults while pending — the body is held anyway); a
  // non-event entry uses the caller's prop. TODO(collision-D): when settings
  // ships genderPolicy/eventFields in participant-config, resolveWizardConfig's
  // merge base must become the INCOMING `config` prop rather than
  // DEFAULT_WIZARD_CONFIG, so a future caller-passed config isn't clobbered
  // by the event resolve at this site.
  return (
    <ProfileWizardInner
      key={childKey ?? 'self'}
      config={(eventId ? eventConfig : null) ?? config}
      configPending={configPending}
      eventQuery={eventId}
      guardianEntry={guardianEntry}
      hubEntry={hubEntry}
      childKey={childKey}
    />
  )
}

/** The account edit hub — where a `?hub=1` entry returns on save/back. */
const HUB_PATH = '/profile/edit'

function ProfileWizardInner({ config, configPending, eventQuery, guardianEntry, hubEntry, childKey }: {
  config: WizardConfig
  configPending: boolean
  eventQuery: string | null
  /** Guardian-claimed entry context (S2 slice) — swaps the step-1 intro to
   *  the account-holder framing. Copy only; the flow machine is untouched. */
  guardianEntry: boolean
  /** Account-hub deep link (CMT-2026-09-03-026): Save on every step; save/
   *  back/exit return to /profile/edit instead of flowing onward. */
  hubEntry: boolean
  childKey?: string
}) {
  const { isMobile } = useShellChrome()
  const { t, lang, dir } = useLocale()
  // Back chevron encodes direction — swapped in code, never CSS-mirrored
  // (README §RTL).
  const backChev = dir === 'rtl' ? '›' : '‹'
  const navigate = useNavigate()
  const { stepSlug } = useParams()

  const isChild = !!childKey
  // Child record is a sync read from the P2 store; null = unknown key.
  const childRecord = isChild ? listChildren().find((c) => c.key === childKey) ?? null : null

  // Route → step (/…/step-1..5, /…/complete).
  const isDone = stepSlug === 'complete'
  const stepMatch = /^step-([1-5])$/.exec(stepSlug ?? '')
  const step = stepMatch ? Number(stepMatch[1]) : 0

  const [draft, setDraft] = useState<WizardDraft>(() =>
    childRecord ? childDraftFor(childRecord) : EMPTY_DRAFT)
  // HYDRATION HOLD (CMT-2026-09-03-026): TRUE for the SELF subject until the
  // mount-time draft load settles — the body/footer render nothing (the
  // configPending pattern) instead of EMPTY editable fields over a profile
  // that exists (Duncan's blank-stepper defect). A child seeds synchronously
  // (childDraftFor above) — never held.
  const [draftPending, setDraftPending] = useState(!isChild)
  // FAIL-CLOSED IN HUB MODE (adversarial review F1): true when the self-draft
  // load FAILED or found no session — the draft on screen is NOT the saved
  // profile. A hub entry then bails back to the hub (which owns the honest
  // error + retry screen) instead of offering a primary SAVE whose one click
  // would write EMPTY identity attributes to Cognito and REPLACE the whole
  // extras record (EC contact / nationality / residence / shirt wiped). The
  // non-hub flow keeps its fail-open posture (fields + honest loadNote).
  const [loadFailed, setLoadFailed] = useState(false)
  const [loadNote, setLoadNote] = useState<string | null>(null)
  // Legacy marry-up (CMT-035): true when this wizard was opened pre-filled from
  // a matched legacy account, so it shows the "review your details" banner.
  const [reviewLegacy, setReviewLegacy] = useState(false)
  const [saveState, setSaveState] = useState<'idle' | 'saving'>('idle')
  const [saveError, setSaveError] = useState<string | null>(null)
  const [helpKey, setHelpKey] = useState<'dob' | 'gender' | 'ec' | null>(null)
  // Typeahead queries live beside the draft — free text is NEVER persisted
  // (canon §11.10); only list picks write nationality/residence/city.
  const [natQuery, setNatQuery] = useState<string | null>(null)
  const [resQuery, setResQuery] = useState<string | null>(null)
  const [cityQuery, setCityQuery] = useState<string | null>(null)
  const [natOpen, setNatOpen] = useState(false)
  const [resOpen, setResOpen] = useState(false)
  const [cityOpen, setCityOpen] = useState(false)
  // Trunk-zero strip flags: the note shows on the paste/keystroke that
  // stripped the 0, though the stored value no longer carries it (§5.3).
  const [phStripped, setPhStripped] = useState(false)
  const [ecStripped, setEcStripped] = useState(false)
  // Guardian (account-holder) draft — prefill source for a CHILD subject
  // ("Same as mine", "Use me"). Best-effort; absent guardian data simply
  // renders no prefill affordance (never a fabricated value).
  const [guardian, setGuardian] = useState<WizardDraft | null>(null)
  // PRE-SAVE ELIGIBILITY GATE (BOARDROOM-2026-08-28-194055 items 3+4): the
  // eligibility contracts IN HAND for the SELF subject's held tickets —
  // readiness carry (invitees) + purchaser wire fallback. Best-effort: no
  // contract → the gate skips silently and the SERVER net enforces.
  const [eligContracts, setEligContracts] = useState<HeldEligibilityContract[]>([])
  // The reject copy shown in the amber blocked bar when the draft clearly
  // fails a held ticket's contract at Save — cleared when gender/DOB change.
  const [eligBlock, setEligBlock] = useState<string | null>(null)
  // Purchaser wire (nullable — tests render the wizard outside DataProvider).
  const liveData = useDataOptional()
  // IDENTITY-FIELD EDIT LOCK (LOCKED rule PROGRAMME-RULE-IDENTITY-FIELD-EDIT-
  // LOCK-BY-VERIFICATION-LEVEL-V1; my-laptop scope per dispatch
  // BOARDROOM-2026-08-29-114614 BUILD 3). The SELF subject's verification
  // level off `GET /me/identity-verification`: 3 = self-attested (default —
  // today's behaviour), <= 2 = event-ID-verified or above ⇒ the identity-core
  // steps render READ-ONLY. Starts at 3 and only ever moves on a successful
  // read (fail-open: L3 is the rule's default state, and a read failure must
  // never lock a user out of their own profile — loadIdentityVerificationLevel
  // already resolves 3 on any failure; the .catch is defensive belt-and-braces).
  const [identityLevel, setIdentityLevel] = useState<IdentityVerificationLevel>(3)
  // Mirror of `identityLevel <= 2` readable from async closures that settle in
  // arbitrary order (the legacy-prefill commit below): React state reads in a
  // .then callback are stale-by-closure; the ref always carries the latest
  // known lock (adversarial review P1-1b).
  const identityLockedRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    if (isChild) {
      // The child draft was seeded synchronously; load the ACCOUNT HOLDER's
      // draft as the guardian prefill source only.
      loadSelfDraft()
        .then((self) => { if (!cancelled && self) setGuardian(self) })
        .catch(() => { /* prefills stay absent — never fabricated */ })
      return () => { cancelled = true }
    }
    // CMT-035 marry-up: if login flagged a legacy pre-fill (one-shot marker),
    // load the legacy draft for review; otherwise the normal self draft. On a
    // legacy miss (marker set but nothing came back) fall back to the self draft
    // so the user is never stranded on an empty form they expected pre-filled.
    const prefillLegacy = takeLegacyPrefillPending()
    async function load() {
      try {
        if (prefillLegacy) {
          const legacy = await loadLegacyDraft()
          if (cancelled) return
          if (legacy) {
            // IDENTITY LOCK × LEGACY (adversarial review P1-1b): at level <= 2
            // the legacy prefill must NOT overwrite the identity fields — those
            // are check-in-verified and the locked save would silently drop the
            // prefill anyway, so displaying it under the "verified" note would
            // be a lie. Non-identity prefill stays. Functional update: keep the
            // draft's CURRENT identity fields (persisted truth once the level
            // effect's re-sync has landed; if the lock lands after this commit,
            // that same re-sync overwrites the legacy identity — every settle
            // order converges on persisted truth).
            setDraft((d) => (identityLockedRef.current ? withIdentityFrom(legacy, d) : legacy))
            setReviewLegacy(true)
            return
          }
        }
        const self = await loadSelfDraft()
        if (cancelled) return
        if (self) {
          setDraft(self)
          return
        }
        // NO SESSION at load. The note below is ONLY the transient pre-redirect
        // state (Duncan rule CMT-2026-09-03-026) — never a resting state where
        // a signed-in user edits into the void. Compose with the self-heal
        // lane: try the refresh-token heal FIRST; on success reload the real
        // draft (no redirect, no loadFailed — saved truth lands on screen).
        // Every early return below is INSIDE the try, so the finally still
        // releases the hydration hold — the hold simply extends over the heal
        // round-trip.
        setLoadNote(t('wizard.loadNote.noSession'))
        const health = await ensureLiveSession()
        if (cancelled) return
        if (health === 'healed') {
          const healed = await loadSelfDraft()
          if (cancelled) return
          if (healed) {
            setDraft(healed)
            setLoadNote(null)
            return
          }
        }
        if (health === 'dead') {
          // Signed-out beats the hub error screen: the VISIBLE sign-in
          // redirect wins on a genuinely dead session — loadFailed stays
          // false so the hub bail's <Navigate> never races the
          // location.replace. The hub bail handles NON-session load failures.
          redirectToSignIn()
          return
        }
        // Unhealed (offline-indeterminate, or a heal race that still yielded
        // no draft): nothing on screen is saved truth — fail closed for a hub
        // entry, exactly like the plain no-session case.
        setLoadFailed(true)
      } catch {
        if (!cancelled) {
          setLoadNote(t('wizard.loadNote.loadFail'))
          setLoadFailed(true)
        }
      } finally {
        // Release the hydration hold on EVERY settle path (legacy commit,
        // self commit, no-session, failure) — held forever would dead-end the
        // flow; a failed read falls open to the honest loadNote + empty form.
        if (!cancelled) setDraftPending(false)
      }
    }
    void load()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Gate inputs for the SELF subject: gather the in-hand contracts once on
  // open (holdings + readiness carry + purchaser-wire fallback). Failure /
  // absence contributes nothing — never a blocked save on a guess.
  // GUARDIAN-CLAIMED seats are EXCLUDED outright (S2 slice, BOARDROOM-2026-
  // 09-03-052111 / CMT-2026-09-03-020): on those seats the ticket's contract
  // applies to the CHILD — the child-confirm step evaluates it against the
  // child's own DOB/gender — so the PARENT's profile must never be warned
  // against it (Duncan's live-walk defect: the parent's adult DOB warned
  // against the child ticket's 13–15 band here). The filter is per-TICKET,
  // regardless of entry path, so a parent's OWN-seat entry in the same event
  // is also never cross-warned by the child seat's band.
  useEffect(() => {
    if (isChild) return
    let cancelled = false
    // STALE-RECORD GUARD on the guardian read (review sweep R4 — the same
    // PR#124 P1-2 kind-guard GetReady applies): the contract rows thread the
    // holdings wire's own `kind` (fetchHeldEligibilityContracts reads the
    // SAME /me/holdings the guardian journey keys on), so an EXPLICIT
    // kind='self' seat keeps its contract in the gate even when a stale
    // device-local guardian record lingers (revoke → self-reassign) — only a
    // record that APPLIES (non-self / no wire signal) suppresses it.
    // DECLARED-PERMISSION managed seats are excluded on the SAME reasoning
    // (dispatch BOARDROOM-2026-09-07-103021): the get-ready card now routes
    // the parent-profile step into THIS wizard from those cards
    // (?event=X&guardian=1), and the child seat's contract (wire kind='child'
    // or a local child-identity record) applies to the CHILD — warning the
    // parent's own DOB/gender against it here would recreate the defect one
    // screen deeper. Advisory-filter input only; saves were never blocked.
    fetchHeldEligibilityContracts(liveData?.tickets ?? [])
      .then((c) => {
        if (cancelled) return
        setEligContracts(c.filter((x) =>
          !guardianRecordApplies(x.ticketId, x.kind)
          && x.kind !== 'child'
          && !declaredChildRecordApplies(x.ticketId, x.kind),
        ))
      })
      .catch(() => { /* no contracts in hand — the server net enforces */ })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Level read for the SELF subject (a managed CHILD's device-local record is
  // never check-in-verified on this surface — the child flow stays unlocked).
  useEffect(() => {
    if (isChild) return
    let cancelled = false
    loadIdentityVerificationLevel()
      .then((lvl) => {
        if (cancelled) return
        identityLockedRef.current = lvl <= 2
        setIdentityLevel(lvl)
        if (lvl > 2) return
        // LOCK-FLIP WINDOW (adversarial review P1-1a): the level fetch can
        // resolve AFTER the draft became editable — any in-flight identity
        // edits (or a legacy prefill that settled first) would then sit under
        // the "verified at check-in" note as if they WERE the verified
        // values, be silently omitted by the locked save, and still render on
        // the receipt as saved. RE-SYNC the identity fields from the
        // persisted profile so the locked steps — and the receipt, which
        // renders summaryRows over this same draft — display persisted truth
        // only. Non-identity fields (still editable) are untouched.
        loadSelfDraft()
          .then((self) => {
            if (cancelled || !self) return
            setDraft((d) => withIdentityFrom(d, self))
          })
          .catch(() => {
            // Re-sync failure: the draft keeps whatever it holds. Blanking
            // here could wipe CORRECT identity data the mount-time
            // loadSelfDraft delivered moments earlier (same call, transient
            // divergence), which is worse than the residual display risk —
            // the save still omits the identity attributes either way.
          })
      })
      .catch(() => { /* fail-open — stays 3, self-edit never locked on a read failure */ })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // A changed gender/DOB invalidates the shown verdict — re-evaluated on Save.
  useEffect(() => {
    setEligBlock(null)
  }, [draft.gender, draft.dobD, draft.dobM, draft.dobY])

  // IDENTITY-CORE SET on this surface = name (step 1) + DOB + gender (step 2).
  // Nationality is deliberately EXCLUDED (ground-truthed decision): on this
  // surface it persists only in the DEVICE-LOCAL extras record
  // (profileStore.ts — the consumer pool has no attribute for it), it is never
  // part of the roster/check-in payload the verification event ratifies
  // (emitRoster carries name/gender/birthdate only, app.ts), so locking it
  // would freeze a fact NO verification event ever attested. Phone, residence/
  // city, emergency contact and preferences stay editable at every level.
  //
  // HONESTY NOTE (server-side seam, flagged not skipped): profile writes go
  // DIRECT from this browser to Cognito (profileStore.saveSelfDraft), so this
  // gate — and the matching attribute omission in saveSelfDraft — is UX
  // enforcement, NOT a security boundary. Real L2 enforcement needs either
  // my-api-mediated profile writes or a Cognito pre-update trigger
  // (infra-owned); that seam is a boardroom escalation, not built here.
  const identityLocked = !isChild && identityLevel <= 2

  const set = <K extends keyof WizardDraft>(k: K, v: WizardDraft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }))

  const policy = config.genderPolicy
  const fields = config.eventFields
  // Question toggles (CMT-2026-08-29-022): OFF ⇒ never rendered, never
  // required. A child never asks language regardless, so its preferences step
  // stands or falls on the shirt flag alone.
  const { askLanguage, askShirt } = config
  const prefsVisible = isChild ? askShirt : askLanguage || askShirt
  // Total VISIBLE steps — only the preferences step can drop, so ids 1–4 keep
  // their positions and no eyebrow/progress off-by-one is possible.
  const total = prefsVisible ? MAX_STEP : MAX_STEP - 1
  // Organiser display name for policy notices — [demo] `Apex Games` slot in
  // the canon strings; real value arrives with the event's org record.
  const organiser = config.organiser ?? 'The organiser'
  // Age + suggested junior size are DERIVED from the draft's own DOB.
  const age = isChild ? ageFromDob(draft) : null
  const suggested = isChild ? suggestedJuniorShirt(age) : null
  const gateCtx = useMemo(
    () => ({
      genderPolicy: policy, eventFields: fields,
      subject: (isChild ? 'child' : 'self') as 'child' | 'self',
      suggestedShirt: suggested,
      // Hidden questions are NEVER required to advance (CMT-2026-08-29-022).
      askLanguage, askShirt,
      // Locked identity fields COUNT AS ANSWERED (they are, by definition,
      // verified) — steps 1–2 must never dead-end an L2 participant.
      identityLocked,
    }),
    [policy, fields, isChild, suggested, askLanguage, askShirt, identityLocked],
  )

  const base = isChild ? `/profile/children/${childKey}` : '/get-ready/profile'
  const exitTo = hubEntry ? HUB_PATH : isChild ? '/profile' : '/get-ready'
  // ONE hold for the body/footer: the event config in flight (CMT-2026-08-29-
  // 022) OR the self draft hydrating (CMT-2026-09-03-026) — either way the
  // chrome stays and no field renders until the truth is in hand.
  const held = configPending || draftPending
  // In-flow navigation keeps the event scope (`?event=`) so the toggles hold
  // across steps + the receipt (CMT-2026-08-29-022) — and the guardian entry
  // flag (S2 slice) so a back-navigation to step 1 keeps the account-holder
  // copy — and the hub flag (CMT-2026-09-03-026) so redirects/clamps keep the
  // return-to-hub contract.
  const suffixParts = [
    eventQuery ? `event=${encodeURIComponent(eventQuery)}` : null,
    guardianEntry ? 'guardian=1' : null,
    hubEntry ? 'hub=1' : null,
  ].filter((p): p is string => p !== null)
  const eventSuffix = suffixParts.length ? `?${suffixParts.join('&')}` : ''

  // Unknown child key (deep link to a record this device has never seen) —
  // honest redirect to Profile, never an empty form that saves nowhere.
  if (isChild && !childRecord) return <Navigate to="/profile" replace />
  // FAIL-CLOSED hub bail (adversarial review F1): the saved profile could
  // not be loaded, so the draft on screen is EMPTY — a hub entry's primary
  // SAVE would wipe the real profile in one click. Return to the hub, which
  // owns the honest error + retry screen. Non-hub entries keep today's
  // fail-open flow (fields + loadNote).
  if (hubEntry && loadFailed) return <Navigate to={HUB_PATH} replace />
  if (!isDone && step === 0) return <Navigate to={`${base}/step-1${eventSuffix}`} replace />
  // A step-5 deep link while the preferences step is dropped clamps to the
  // LAST REAL step — the least-surprising landing (the URL meant "the final
  // step"; the receipt would wrongly imply a save happened). SUSPENDED while
  // the event config is in flight (P1-1): `total` is not yet this event's
  // truth, so clamping early could redirect against the wrong step count —
  // the hold renders nothing anyway, and the clamp fires on resolve.
  if (!configPending && !isDone && step > total) return <Navigate to={`${base}/step-${total}${eventSuffix}`} replace />

  const needs = isDone ? [] : stepNeeds(step, draft, gateCtx, t)
  const ok = needs.length === 0
  const complete = allValid(draft, policy, identityLocked)

  // FAIL-FAST ELIGIBILITY PRE-CHECK (Duncan rulings BOARDROOM-2026-08-29-
  // 120259 #1 + ADDENDUM 2 123126): the mismatch surfaces AT THE MOMENT the
  // disqualifying selection is made — inline on the gender field (gender axis:
  // "This ticket is for a male participant", event-genderized from the
  // ticket's own contract) and inline on the DOB field (age axis: the
  // ticket's real band copy). PURELY DERIVED per render from the draft +
  // the contracts already in hand (readiness/holdings carry — nothing new is
  // fetched, nothing fabricated), so it fires on the keystroke/tap that
  // creates the mismatch and clears on the one that resolves it.
  // TICKET-SCOPED: only the contracts of the wizard's `?event=` entry context
  // are consulted — an account-wide entry (no event param) fires NO pre-check
  // (the server 422 at profile-complete stays the backstop), and a contract
  // with no restriction on an axis never fires (eligibilityGate's allow-on-
  // unknown posture; the age axis additionally needs the wire's raceDay
  // anchor — honest absence means no age pre-check, never a derived band).
  const inlineMismatch = (() => {
    if (isChild || !eventQuery) return null
    for (const c of eligContracts) {
      if (c.eventId !== eventQuery) continue
      const v = eligibilityGate({ gender: draft.gender, birthdate: dobIso(draft) }, c.eligibility, c.raceDay)
      if (!v.ok) {
        const copy = eligibilityMismatchCopy(v.kind, c.eligibility)
        return {
          kind: v.kind,
          copy: copy ? t(copy.key, copy.params) : v.message,
          ticketId: c.ticketId,
          eventName: c.eventName,
        }
      }
    }
    return null
  })()

  const goStep = (n: number) => navigate(`${base}/step-${n}${eventSuffix}`)
  const exitFlow = () => navigate(exitTo)
  // Hub deep link (CMT-2026-09-03-026): Back returns to the HUB from any
  // step — a single-field edit never walks backwards through the flow.
  const back = () => (hubEntry || step <= 1 ? exitFlow() : goStep(step - 1))
  const next = () => { if (ok) goStep(step + 1) }
  const save = async () => {
    if (!ok || saveState === 'saving') return
    // INSTANT PRE-SAVE FEEDBACK (BOARDROOM-2026-08-28-194055 item 3): for the
    // SELF subject, run the R3 gate against every held ticket's IN-HAND
    // contract and WARN on a clear gender/age mismatch — but the save always
    // proceeds. ADVISORY BY DESIGN (PR #83 review P1): a held seat can be a
    // PROXY holding (guestparent — the parent holds the child's seat), so a
    // seat mismatch must never hold the ACCOUNT profile hostage. Enforcement
    // is the server's per-seat 422 at profile-complete; this bar just makes
    // the likely reject visible before it lands on the get-ready card.
    if (!isChild) {
      for (const c of eligContracts) {
        const v = eligibilityGate(
          { gender: draft.gender, birthdate: dobIso(draft) },
          c.eligibility,
          c.raceDay,
        )
        if (!v.ok) {
          const copy = eligibilityRejectCopy(v.kind, c.eligibility)
          setEligBlock(copy ? t(copy.key, copy.params) : v.message)
          break // show the first mismatch; save continues regardless
        }
      }
    }
    setSaveState('saving')
    setSaveError(null)
    try {
      if (isChild && childRecord) {
        // Child subject → the P2 child store (`child_{key}` record). The
        // effective junior size (age-suggested unless overridden) is what
        // gets recorded — same fact the receipt shows. NO server write: my-api
        // has no child-profile contract (// TODO WIRE, assignStore.ts).
        saveChildProfile(childRecord.key, { ...draft, shirt: draft.shirt || suggested || '' })
      } else {
        // SAVE-TIME LOCK RE-CHECK (adversarial review P2-1, honest-user path):
        // the level can flip L3→L2 while this session sits on the wizard
        // (check-in happens mid-session). Re-fetch at Save; if it comes back
        // locked when the session thought unlocked, treat the save as locked —
        // identity attributes omitted — and SURFACE the lock: the level state
        // flips (locked note + disabled fields on any revisit) and the draft's
        // identity fields re-sync from the persisted profile so the receipt
        // asserts only what actually saved (P1-1c). FAIL-OPEN on error, like
        // every level read: loadIdentityVerificationLevel resolves 3 on its
        // own failures; the .catch guards a rejecting test double.
        let lockedNow = identityLocked
        let toSave = draft
        if (!identityLocked) {
          const lvl = await loadIdentityVerificationLevel().catch(() => 3 as const)
          if (lvl <= 2) {
            lockedNow = true
            identityLockedRef.current = true
            setIdentityLevel(lvl)
            const self = await loadSelfDraft().catch(() => null)
            // No persisted read ⇒ EMPTY identity fields: an honest "could not
            // read the verified values" beats displaying edits the locked
            // save is about to omit.
            toSave = withIdentityFrom(draft, self ?? EMPTY_DRAFT)
            setDraft(toSave)
          }
        }
        // identityLocked ⇒ the identity-core Cognito attributes are OMITTED
        // from the write (profileStore keeps the write honest with the
        // read-only render — UX enforcement; see the seam note above).
        await saveSelfDraft(toSave, { identityLocked: lockedNow })
        // Record profile completion on the server for held tickets (when the
        // profile is complete) + signal Get ready to re-read readiness, so the
        // home reflects it on return rather than a stale pre-save snapshot.
        // Completion recomputes over what was ACTUALLY saved (a save-time
        // lock flip changes both inputs).
        await onProfileSaved(allValid(toSave, policy, lockedNow))
      }
      setSaveState('idle')
      // Hub entry returns to the HUB (CMT-2026-09-03-026, spec 3) — the hub
      // re-reads the saved profile; the receipt is the FLOW's terminal, not
      // the hub's.
      navigate(hubEntry ? HUB_PATH : `${base}/complete${eventSuffix}`)
    } catch (e) {
      setSaveState('idle')
      setSaveError(e instanceof Error ? e.message : t('wizard.save.failed'))
      // Save died on a DEAD session (profileStore's 'not signed in — nothing
      // was saved'): the error line is only the transient pre-redirect state
      // (CMT-2026-09-03-026). Heal-first via the shared lane; a genuinely
      // unrefreshable session redirects to sign-in with this step preserved,
      // so the user signs back in and lands on their edits' step. The draft
      // fields and device-local records survive — nothing here touches
      // localStorage.
      if (!isChild && loadSession() === null) {
        void ensureLiveSession().then((health) => {
          if (health === 'dead') redirectToSignIn()
        })
      }
    }
  }

  // --- selections ---
  const natSel = draft.natIso ? countryByIso(draft.natIso) : null
  const resSel = draft.resIso ? countryByIso(draft.resIso) : null
  // City is valid once a residence is picked and a city is SET (a set value is
  // always a deliberate typeahead pick — see wizardModel.cityValid).
  const cityIsValid = !!(resSel && draft.city)
  const natQ = natQuery ?? draft.nationality
  const resQ = resQuery ?? draft.resCountry
  const cityQ = cityQuery ?? draft.city
  const natRows = natOpen && !natSel ? nationalityMatches(natQ) : []
  const resRows = resOpen && !resSel ? residenceMatches(resQ) : []
  const cityRows = cityOpen && resSel && !cityIsValid ? cityMatches(resSel, cityQ) : []
  // "Use my entry" affordance (CMT-100427): when what's typed isn't a known
  // dataset city, offer it as an explicit pick so an unrecognised place is never
  // a silent dead end. Only once there's enough to be a real name (>= 2 chars)
  // and it isn't already an exact match already shown in the list.
  const cityTyped = cityQ.trim()
  const showCityCustom =
    cityOpen && !!resSel && !cityIsValid && cityTyped.length >= 2 && !cityKnown(resSel, cityTyped)

  // Typeahead COMMIT handlers (BOARDROOM-2026-08-29-032706 F3): the residence/
  // city comboboxes "resisted selection" — a pick could be lost and the field
  // never committed. Extracted so mouse (onClick), keyboard (Enter → first
  // match) and the mobile-safe onMouseDown path all commit through ONE code
  // path. Nationality mirrors it for consistency.
  const pickNationality = (c: RefCountry) => {
    // The DEMONYM is what gets stored (README §Flows B).
    setDraft((d) => ({ ...d, nationality: c.dem, natIso: c.iso }))
    setNatQuery(c.dem)
    setNatOpen(false)
  }
  const pickResidence = (c: RefCountry) => {
    // Residence drives the dial code; city resets with it.
    setDraft((d) => ({ ...d, resCountry: c.n, resIso: c.iso, city: '', dial: `${c.iso} +${c.d}` }))
    setResQuery(c.n)
    setCityQuery('')
    setResOpen(false)
  }
  const pickCity = (c: string) => {
    setDraft((d) => ({ ...d, city: c }))
    setCityQuery(c)
    setCityOpen(false)
  }

  // Residence drives the dial code (README §Flows B).
  const ph = phoneState(draft.dial || (resSel ? `${resSel.iso} +${resSel.d}` : DEFAULT_DIAL), draft.phone, t)
  const ecp = phoneState(draft.ecDial || DEFAULT_DIAL, draft.ecPhone, t)
  const phDialValue = draft.dial || (resSel ? `${resSel.iso} +${resSel.d}` : DEFAULT_DIAL)
  const ecDialValue = draft.ecDial || DEFAULT_DIAL

  // --- chrome geometry ---
  // The shell content area carries page padding (AppShell §3); flows fill the
  // content area edge-to-edge (DC: content padding 0 while a flow is active).
  // Cancelled here with matching negative margins so the shell chrome file
  // stays untouched by this work unit.
  const pad = isMobile
    ? { margin: '-18px -16px -26px', height: 'calc(100% + 44px)' }
    : { margin: '-26px -30px -36px', height: 'calc(100% + 62px)' }
  const headPad = isMobile ? '12px 16px 10px' : '18px 26px 12px'
  const bodyPad = isMobile ? '16px 16px 20px' : '20px 26px 24px'
  const footPad = isMobile ? '10px 16px 12px' : '14px 26px 16px'
  const blockPadX = isMobile ? 16 : 26

  // Receipt header reads SAVED, never `Step 5 of 5` (README §Flows B).
  // While the event config OR the draft hydration is in flight (`held`) the
  // count + progress are WITHHELD (adversarial review F3) — never a "Step N
  // of 5" / progress bar / "New profile" flash over an account whose saved
  // profile simply hasn't arrived yet (the "no shown-then-corrected" rule,
  // CMT-2026-08-29-022 / CMT-2026-09-03-026).
  const stepnum = held ? '' : isDone ? t('wizard.saved') : t('flow.step', { n: step, m: total })
  const progressPct = held ? 0 : isDone ? 100 : (step / total) * 100
  // Context line: no event context exists on either route (a child opened
  // from Profile has no ticket here — QA 8.7) → honest `Account profile`,
  // never an invented event name.
  const context = t('wizard.context.account')

  // The SUBJECT's name — step 2+ header (a parent filling in for several
  // children must always know which one).
  const who = `${draft.first} ${draft.last}`.trim() || t('wizard.newProfile')
  // Support pre-fill name (review P2-5): the `who` fallback is the
  // `wizard.newProfile` UI placeholder — a display crutch, NOT the
  // participant's name — and must never ride into the support email. Absent
  // name ⇒ undefined, and the seam simply omits the Participant line.
  const supportName = `${draft.first} ${draft.last}`.trim() || undefined
  // Child display name for headings/copy; the record's own name, never a
  // prototype person.
  const kid = (draft.first || childRecord?.first || '').trim()
  const eyebrow = isDone ? '' : isChild
    ? ['', '',
        t('wizard.eyebrow.child.details', { name: kid }),
        t('wizard.eyebrow.child.natres', { name: kid }),
        t('wizard.eyebrow.ec'),
        t('wizard.eyebrow.child.kit', { name: kid })][step] ?? ''
    : ['', '',
        t('wizard.eyebrow.personal'),
        t('wizard.eyebrow.natres'),
        t('wizard.eyebrow.ec'),
        t('wizard.eyebrow.prefs')][step] ?? ''

  const genderMode = GENDER_MODES[policy]
  const showSelfDescribe = policy === 'flagged' && draft.gender === SELF_DESCRIBE_PILL
  const showOpenNotice = policy === 'third' && draft.gender === 'Open'

  const addrLabel = fields.address === 'required' ? t('wizard.addr.label') : t('wizard.addr.labelOptional')
  const phoneLabel = fields.phone === 'required' ? t('wizard.phone.label') : t('wizard.phone.labelOptional')

  // Guardian prefills (child subject, README §Flows B): offered on the EMPTY
  // state only — never able to overwrite a chosen value, never fabricated
  // (they render only when the account holder's own record carries the data).
  const guardianName = guardian ? `${guardian.first} ${guardian.last}`.trim() : ''
  const canCopyNat = !!(isChild && guardian?.nationality && guardian.natIso && !draft.nationality)
  const canCopyRes = !!(isChild && guardian?.resCountry && guardian.resIso && !draft.resCountry)
  const canCopyEc = !!(isChild && guardianName && !draft.ecName)
  // Junior shirt: the effective value is the pick, else the age suggestion.
  const effShirt = isChild ? (draft.shirt || suggested || '') : draft.shirt
  // The account's language preference — ONE field serves interface + comms in
  // V1 (PRODUCT-RULES §Localisation); a child inherits it.
  const accountLang = lang === 'ar' ? 'العربية' : 'English'

  return (
    <div id="wizard.root" style={{ ...pad, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* HEADER — back ‹, context, Step {n} of 5 / SAVED, 5px progress */}
      <div id="wizard.head" data-testid="wiz-head" style={{ flex: 'none', padding: headPad, borderBottom: '1px solid var(--hairline-07)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <button
            data-testid="wiz-back"
            onClick={exitFlow}
            aria-label={t('flow.close')}
            style={{ border: 0, background: 'transparent', color: 'var(--text-muted)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer', minHeight: 44, padding: 0 }}
          >{backChev}</button>
          <span data-testid="wiz-context" style={{ fontSize: 12, color: 'var(--text-muted)', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{context}</span>
          {/* Step 2+ carries the SUBJECT's name (README §Flows B). Held with
              the body (F3): "New profile" must never flash over an account
              whose saved name simply hasn't hydrated yet. */}
          {!held && !isDone && step > 1 && (
            <span data-testid="wiz-who" style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', flex: 'none', maxWidth: '38%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{who}</span>
          )}
          <span data-testid="wiz-stepnum" style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', flex: 'none' }}>{stepnum}</span>
        </div>
        <div style={{ height: 5, borderRadius: 20, background: 'var(--progress-track)', overflow: 'hidden', marginTop: 10 }}>
          <div data-testid="wiz-progress" style={{ height: '100%', borderRadius: 20, background: 'var(--accent)', width: `${progressPct}%` }} />
        </div>
      </div>

      {/* BODY — scrollable. Held EMPTY while an event config is in flight
          (CMT-2026-08-29-022) OR the self draft is hydrating from the saved
          profile (CMT-2026-09-03-026): the chrome stays and no EMPTY field
          ever renders over a profile that exists — the blank-stepper defect. */}
      <div id="wizard.body" style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: bodyPad }}>
        {/* Honest hydration status — a held body is loading, not broken. */}
        {draftPending && (
          <div data-testid="wiz-draft-loading" role="status" style={{ fontSize: 11.5, color: 'var(--text-faint)' }}>
            {t('wizard.loadingProfile')}
          </div>
        )}
        {!held && loadNote && (
          <div role="status" style={{ fontSize: 11.5, color: 'var(--text-faint)', marginBottom: 12 }}>{loadNote}</div>
        )}

        {/* Legacy marry-up review banner (CMT-035): shown when the wizard opened
            pre-filled from a matched legacy account. Nothing is auto-committed —
            the user reviews these details and Saves as normal. Under the
            identity lock (level <= 2) the REWORDED variant renders instead
            (P1-1b): the standard copy invites edits over fields that are
            disabled — the locked variant scopes the review ask to the
            still-editable details. */}
        {!held && reviewLegacy && !isDone && (
          <div
            data-testid="wiz-legacy-review"
            role="status"
            style={{
              fontSize: 12, lineHeight: 1.5, color: 'var(--text-secondary)',
              background: 'rgba(18,196,172,.08)', border: '1px solid rgba(18,196,172,.3)',
              borderRadius: 10, padding: '10px 12px', marginBottom: 14,
            }}
          >
            {t(identityLocked ? 'wizard.legacyReview.identityLocked' : 'wizard.legacyReview')}
          </div>
        )}

        {!held && !isDone && step > 1 && (
          <>
            <div style={eyebrowStyle}>{eyebrow}</div>
            <h1 style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 18px' }}>{who}</h1>
          </>
        )}

        {/* STEP 1 — name */}
        {!held && step === 1 && (
          <div id="wizard.step1">
            <h1 style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 6px' }}>
              {isChild ? t('wizard.step1.heading.child', { name: kid || who }) : t('wizard.step1.heading.self')}
            </h1>
            {/* GUARDIAN entry (S2 slice, CMT-2026-09-03-020): the parent's
                name is ACCOUNT-HOLDER data — the child's name goes on the bib
                and results, and it lives in the child-confirm step. The
                bib/results sentence must never address the parent here
                (Duncan's live-walk defect). */}
            <p data-testid="wiz-step1-intro" style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, margin: '0 0 20px' }}>
              {isChild
                ? t('wizard.step1.intro.child')
                : guardianEntry ? t('wizard.step1.intro.selfGuardian') : t('wizard.step1.intro.self')}
            </p>
            {/* Identity lock (level <= 2): name is identity-core — read-only
                with the explanatory note. UX enforcement only (see the seam
                note at identityLocked). */}
            {identityLocked && (
              <div data-testid="wiz-identity-locked-note" role="note" style={identityLockedNoteStyle}>
                {t('wizard.identityLocked.note')}
              </div>
            )}
            {/* No person-name placeholders: `Alex` / `Morgan` were prototype
                review people (PRODUCT-RULES §Names) — never shipped. */}
            <label style={labelStyle}>{t('wizard.field.first')}</label>
            <input data-testid="wiz-first" value={draft.first} disabled={identityLocked} onChange={(e) => set('first', e.target.value)} style={{ ...inputStyle, ...(identityLocked ? lockedFieldStyle : null) }} />
            <Gap h={14} />
            <label style={labelStyle}>{t('wizard.field.last')}</label>
            <input data-testid="wiz-last" value={draft.last} disabled={identityLocked} onChange={(e) => set('last', e.target.value)} style={{ ...inputStyle, ...(identityLocked ? lockedFieldStyle : null) }} />
          </div>
        )}

        {/* STEP 2 — Personal details: DOB + gender */}
        {!held && step === 2 && (
          <div id="wizard.step2">
            {/* Identity lock (level <= 2): DOB + gender are identity-core —
                read-only with the explanatory note (same note as step 1: the
                two steps are ONE locked identity set). */}
            {identityLocked && (
              <div data-testid="wiz-identity-locked-note" role="note" style={identityLockedNoteStyle}>
                {t('wizard.identityLocked.note')}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <label style={{ ...labelStyle, margin: 0 }}>{t('wizard.field.dob')}</label>
              <button data-testid="wiz-help-toggle" data-help="dob" onClick={() => setHelpKey(helpKey === 'dob' ? null : 'dob')} style={helpBtnStyle}>?</button>
            </div>
            {helpKey === 'dob' && (
              <div data-testid="wiz-helpbox" style={{ ...helpBoxStyle, margin: '0 0 10px' }}>
                {t('wizard.help.dob')}
              </div>
            )}
            {/* Three explicit selects — DOB is NEVER a single ambiguous DD/MM
                field. Month sized to its content (CMT-2026-08-14-040). */}
            <div style={{ display: 'grid', gridTemplateColumns: '58px max-content 72px', gap: 8, justifyContent: 'start' }}>
              <select data-testid="wiz-dob-day" aria-label={t('assign.field.dob.day')} value={draft.dobD || t('assign.field.dob.day')} disabled={identityLocked} onChange={(e) => set('dobD', e.target.value === t('assign.field.dob.day') ? '' : e.target.value)} style={{ ...selectStyle, ...(identityLocked ? lockedFieldStyle : null) }}>
                {[t('assign.field.dob.day'), ...DAYS].map((d) => <option key={d}>{d}</option>)}
              </select>
              <select data-testid="wiz-dob-month" aria-label={t('assign.field.dob.month')} value={draft.dobM || t('assign.field.dob.month')} disabled={identityLocked} onChange={(e) => set('dobM', e.target.value === t('assign.field.dob.month') ? '' : e.target.value)} style={{ ...selectStyle, ...(identityLocked ? lockedFieldStyle : null) }}>
                {[t('assign.field.dob.month'), ...MONTHS].map((m) => <option key={m}>{m}</option>)}
              </select>
              {/* A child's birth year can be recent; an adult entrant's cannot. */}
              <select data-testid="wiz-dob-year" aria-label={t('assign.field.dob.year')} value={draft.dobY || t('assign.field.dob.year')} disabled={identityLocked} onChange={(e) => set('dobY', e.target.value === t('assign.field.dob.year') ? '' : e.target.value)} style={{ ...selectStyle, ...(identityLocked ? lockedFieldStyle : null) }}>
                {[t('assign.field.dob.year'), ...(isChild ? CHILD_YEARS : SELF_YEARS)].map((y) => <option key={y}>{y}</option>)}
              </select>
            </div>
            {dobFull(draft) && (
              <div data-testid="wiz-dob-echo" style={{ fontSize: 11.5, marginTop: 8, color: 'var(--success)', fontWeight: 700 }}>
                {t('wizard.dob.echo', { date: dobFull(draft) })}
              </div>
            )}
            {/* AGE-AXIS FAIL-FAST (ADDENDUM 2, BOARDROOM-2026-08-29-123126):
                fires the moment the entered DOB fails the scoped ticket's
                carried age band (ticket context + wire band + raceDay anchor
                all present — else no pre-check; the note below still holds
                for the account-wide entry). Carries the same support out +
                sub-states as the gender axis; the flow is NEVER blocked. */}
            {inlineMismatch?.kind === 'age' && (
              <div data-testid="wiz-inline-mismatch" data-kind="age" style={{ marginTop: 12 }}>
                {/* keyed by ticket (P2-1 discipline): the notice seeds its
                    reported sub-state per ticket on mount — a scoped-ticket
                    change must remount it. participantName omits the
                    `wizard.newProfile` placeholder (P2-5). */}
                <EligibilityMismatchNotice
                  key={inlineMismatch.ticketId}
                  ticketId={inlineMismatch.ticketId}
                  eventName={inlineMismatch.eventName}
                  copy={inlineMismatch.copy}
                  reason="age"
                  participantName={supportName}
                />
              </div>
            )}
            {/* No LOCAL age-band gate beyond the scoped pre-check above: from
                Profile there is no ticket context (QA 8.7); band checks live
                in the assign flow. */}
            <Gap h={20} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9 }}>
              <label style={{ ...labelStyle, margin: 0 }}>{t('wizard.field.gender')}</label>
              <button data-testid="wiz-help-toggle" data-help="gender" onClick={() => setHelpKey(helpKey === 'gender' ? null : 'gender')} style={helpBtnStyle}>?</button>
            </div>
            {helpKey === 'gender' && (
              <div data-testid="wiz-helpbox" style={{ ...helpBoxStyle, margin: '0 0 10px' }}>{t(genderMode.helpKey, { org: organiser })}</div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {genderMode.opts.map((g) => (
                <button key={g} data-testid="wiz-gender-pill" data-value={g} disabled={identityLocked} onClick={() => set('gender', g)} style={{ ...pillStyle(draft.gender === g), ...(identityLocked ? { ...lockedFieldStyle, cursor: 'not-allowed' } : null) }}>{g}</button>
              ))}
            </div>
            {showSelfDescribe && (
              <div style={{ marginTop: 12 }}>
                <label style={labelStyle}>{t('wizard.selfDescribe.label')}</label>
                {/* Gender-adjacent free text renders under the gender pills —
                    locked with them (one identity set). */}
                <input data-testid="wiz-self-describe" value={draft.selfText} disabled={identityLocked} onChange={(e) => set('selfText', e.target.value)} placeholder={t('wizard.selfDescribe.placeholder')} style={{ ...inputStyle, ...(identityLocked ? lockedFieldStyle : null) }} />
              </div>
            )}
            {showSelfDescribe && (
              <div data-testid="wiz-gender-notice" style={warnBoxStyle}>
                <span style={{ flex: 'none', fontSize: 14, color: 'var(--warn)' }}>⚠</span>
                <div style={{ fontSize: 11.5, lineHeight: 1.5, color: 'var(--warn)' }}>
                  {t('wizard.gender.flagNotice', { org: organiser })}
                </div>
              </div>
            )}
            {showOpenNotice && (
              <div data-testid="wiz-gender-notice" style={{ ...warnBoxStyle, background: 'rgba(18,196,172,.08)', border: '1px solid rgba(18,196,172,.3)' }}>
                <span style={{ flex: 'none', fontSize: 14, color: 'var(--success)' }}>✓</span>
                <div style={{ fontSize: 11.5, lineHeight: 1.5, color: 'var(--success)' }}>
                  {t('wizard.gender.openNotice.pre')}<b>{draft.gender}</b>{t('wizard.gender.openNotice.post', { org: organiser })}
                </div>
              </div>
            )}
            {/* GENDER-AXIS FAIL-FAST (ruling 1): inline on the gender field,
                the moment the disqualifying pill is picked — event-genderized
                copy off the ticket's own contract, with the support out + its
                two sub-states. Derived per render: switching the pill back
                clears it instantly. Only with a `?event=` ticket context. */}
            {inlineMismatch?.kind === 'gender' && (
              <div data-testid="wiz-inline-mismatch" data-kind="gender" style={{ marginTop: 12 }}>
                {/* keyed by ticket (P2-1 discipline) + placeholder-free
                    participantName (P2-5) — see the age-axis note above. */}
                <EligibilityMismatchNotice
                  key={inlineMismatch.ticketId}
                  ticketId={inlineMismatch.ticketId}
                  eventName={inlineMismatch.eventName}
                  copy={inlineMismatch.copy}
                  reason="gender"
                  participantName={supportName}
                />
              </div>
            )}
          </div>
        )}

        {/* STEP 3 — Nationality & residence */}
        {!held && step === 3 && (
          <div id="wizard.step3">
            <label style={labelStyle}>{t('wizard.nat.label')}</label>
            <input
              data-testid="wiz-nationality"
              value={natQ}
              onChange={(e) => {
                setNatQuery(e.target.value)
                setNatOpen(true)
                setDraft((d) => ({ ...d, nationality: '', natIso: '' }))
              }}
              onFocus={() => setNatOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && natRows.length > 0) { e.preventDefault(); pickNationality(natRows[0]) }
              }}
              placeholder={t('wizard.nat.placeholder')}
              style={inputStyle}
            />
            {natRows.length > 0 && (
              <TypeaheadList testid="wiz-nationality-list">
                {natRows.map((c) => (
                  <button
                    key={c.iso}
                    // onMouseDown keeps the input focused so a mobile tap can't
                    // be lost to a blur/keyboard-dismiss layout shift (F3).
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pickNationality(c)}
                    style={listRowStyle}
                  >
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', flex: 'none' }}>{c.iso3}</span>
                    <span style={{ flex: 'none' }}>{c.dem}</span>
                    <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-faint)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.n}</span>
                  </button>
                ))}
              </TypeaheadList>
            )}
            <div data-testid="wiz-nationality-hint" style={hintStyle(!!natSel)}>
              {natSel
                ? t('wizard.nat.hintOk', { dem: natSel.dem, country: natSel.n, iso3: natSel.iso3 })
                : t('wizard.nat.hintIdle')}
            </div>
            {canCopyNat && guardian && (
              <>
                <Gap h={9} />
                <button
                  data-testid="wiz-nat-prefill"
                  onClick={() => {
                    setDraft((d) => ({ ...d, nationality: guardian.nationality, natIso: guardian.natIso }))
                    setNatQuery(guardian.nationality)
                    setNatOpen(false)
                  }}
                  style={prefillBtnStyle}
                >{t('wizard.prefill.sameAsMine', { value: guardian.nationality })}</button>
              </>
            )}

            <div style={{ height: 18, marginTop: 2, borderTop: '1px solid var(--hairline-07)' }} />
            <div style={{ ...eyebrowStyle, marginBottom: 9 }}>
              {isChild ? t('wizard.res.heading.child', { name: kid || who }) : t('wizard.res.heading.self')}
            </div>
            <label style={labelStyle}>{t('wizard.res.label')}</label>
            <input
              data-testid="wiz-residence"
              value={resQ}
              onChange={(e) => {
                setResQuery(e.target.value)
                setResOpen(true)
                // typing in residence clears the picked country AND city
                setDraft((d) => ({ ...d, resCountry: '', resIso: '', city: '' }))
                setCityQuery('')
              }}
              onFocus={() => setResOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && resRows.length > 0) { e.preventDefault(); pickResidence(resRows[0]) }
              }}
              placeholder={t('wizard.res.placeholder')}
              style={inputStyle}
            />
            {resRows.length > 0 && (
              <TypeaheadList testid="wiz-residence-list">
                {resRows.map((c) => (
                  <button
                    key={c.iso}
                    // onMouseDown keeps the input focused so a mobile tap can't
                    // be lost to a blur/keyboard-dismiss layout shift (F3).
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pickResidence(c)}
                    style={listRowStyle}
                  >
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', flex: 'none' }}>{c.iso3}</span>
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.n}</span>
                  </button>
                ))}
              </TypeaheadList>
            )}
            <div data-testid="wiz-residence-hint" style={hintStyle(!!resSel)}>
              {resSel
                ? t('wizard.res.hintOk', { country: resSel.n, iso3: resSel.iso3 })
                : t('wizard.res.hintIdle')}
            </div>
            {canCopyRes && guardian && !resSel && (
              <>
                <Gap h={9} />
                <button
                  data-testid="wiz-res-prefill"
                  onClick={() => {
                    setDraft((d) => ({
                      ...d, resCountry: guardian.resCountry, resIso: guardian.resIso,
                      city: guardian.city, dial: guardian.dial,
                    }))
                    setResQuery(guardian.resCountry)
                    setCityQuery(guardian.city)
                    setResOpen(false)
                  }}
                  style={prefillBtnStyle}
                >{t('wizard.prefill.sameAsMine', { value: `${guardian.city ? guardian.city + ', ' : ''}${guardian.resCountry}` })}</button>
              </>
            )}
            {natSel && !resSel && (
              // empty-state prefill only — never overwrites a chosen residence
              <button
                data-testid="wiz-residence-same-as-nat"
                onClick={() => {
                  setDraft((d) => ({ ...d, resCountry: natSel.n, resIso: natSel.iso, city: '', dial: `${natSel.iso} +${natSel.d}` }))
                  setResQuery(natSel.n)
                  setCityQuery('')
                  setResOpen(false)
                }}
                style={{ ...prefillBtnStyle, marginTop: 9 }}
              >
                {t('wizard.res.sameAsNat', { country: natSel.n })}
              </button>
            )}
            <Gap h={16} />
            <label id="wiz-city-label" style={labelStyle}>{t('wizard.city.label')}</label>
            {/* City is dependent on the residence country and LOCKED until it
                is chosen (README §Flows B). */}
            <input
              data-testid="wiz-city"
              value={cityQ}
              disabled={!resSel}
              role="combobox"
              aria-expanded={cityRows.length > 0 || showCityCustom}
              aria-controls="wiz-city-list"
              aria-autocomplete="list"
              aria-labelledby="wiz-city-label"
              onChange={(e) => {
                setCityQuery(e.target.value)
                setCityOpen(true)
                setDraft((d) => ({ ...d, city: '' }))
              }}
              // Open the list on focus so tapping the (enabled) field previews
              // the country's top cities immediately — never an empty dead field (F3).
              onFocus={() => resSel && setCityOpen(true)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return
                if (cityRows.length > 0) { e.preventDefault(); pickCity(cityRows[0]) }
                else if (showCityCustom) { e.preventDefault(); pickCity(cityTyped) }
              }}
              placeholder={t('wizard.city.placeholder')}
              style={{ ...inputStyle, opacity: resSel ? 1 : 0.55 }}
            />
            {(cityRows.length > 0 || showCityCustom) && (
              <div id="wiz-city-list" data-testid="wiz-city-list" role="listbox" aria-label="City suggestions" style={listBoxStyle}>
                {cityRows.map((c) => (
                  <button
                    key={c}
                    role="option"
                    aria-selected={draft.city === c}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pickCity(c)}
                    style={{ ...listRowStyle, display: 'block' }}
                  >{c}</button>
                ))}
                {/* Free-text escape hatch — pick your own entry when the place
                    isn't in the dataset, so it never silently blocks (no geo
                    service exists; the dataset is major cities only). */}
                {showCityCustom && (
                  <button
                    data-testid="wiz-city-custom"
                    role="option"
                    aria-selected={false}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pickCity(cityTyped)}
                    style={{ ...listRowStyle, display: 'flex', color: 'var(--text-secondary)' }}
                  >
                    <span style={{ flex: 'none' }}>{t('wizard.city.use')}</span>
                    <span style={{ fontWeight: 800, color: 'var(--text)' }}>&ldquo;{cityTyped}&rdquo;</span>
                  </button>
                )}
              </div>
            )}
            <div data-testid="wiz-city-hint" style={hintStyle(cityIsValid)}>
              {cityIsValid
                ? t('wizard.city.hintOk', { city: draft.city })
                : resSel
                  ? t('wizard.city.hintPick', { country: resSel.n })
                  : t('wizard.city.hintLocked')}
            </div>

            {/* eventFields-driven — anything not requested is never rendered
                (canon §5.3 / §11.4: no "(optional)" filler). */}
            {fields.address !== false && (
              <>
                <Gap h={16} />
                <label style={labelStyle}>{addrLabel}</label>
                <input data-testid="wiz-address" value={draft.addr} onChange={(e) => set('addr', e.target.value)} style={inputStyle} />
              </>
            )}
            {/* Phone is the ACCOUNT HOLDER's field — never asked of a child. */}
            {fields.phone !== false && !isChild && (
              <>
                <Gap h={16} />
                <label style={labelStyle}>{phoneLabel}</label>
                <div style={phoneRowStyle}>
                  <select data-testid="wiz-phone-dial" aria-label="Country code" value={phDialValue} onChange={(e) => set('dial', e.target.value)} style={selectStyle}>
                    {/* Blank placeholder (F2 belt-and-braces): an unmatched value
                        shows blank, never the alphabetically-first country. */}
                    <option value="" />
                    {dialOptions().map((o) => <option key={o}>{o}</option>)}
                  </select>
                  <input
                    data-testid="wiz-phone"
                    value={draft.phone}
                    onChange={(e) => {
                      const f = filterPhoneInput(phDialValue, e.target.value)
                      setPhStripped(f.stripped)
                      set('phone', f.value)
                    }}
                    placeholder="50 123 4567"
                    style={phoneBoxStyle(ph.tone)}
                  />
                </div>
                <div data-testid="wiz-phone-hint" style={phoneHintStyle(ph.tone)}>
                  {phStripped ? t('wizard.phone.stripped') : ph.hint}
                </div>
              </>
            )}
          </div>
        )}

        {/* STEP 4 — Emergency contact (/get-ready/emergency lands here) */}
        {!held && step === 4 && (
          <div id="wizard.step4">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                {isChild ? t('wizard.ec.intro.child', { name: kid || who }) : t('wizard.ec.intro.self')}
              </span>
              <button data-testid="wiz-help-toggle" data-help="ec" onClick={() => setHelpKey(helpKey === 'ec' ? null : 'ec')} style={helpBtnStyle}>?</button>
            </div>
            {/* `Use me` shortcut (README §Flows B) — copies the ACCOUNT
                HOLDER's real details; rendered only when we have them. */}
            {canCopyEc && guardian && (
              <button
                data-testid="wiz-ec-prefill"
                onClick={() => {
                  setDraft((d) => ({
                    ...d, ecName: guardianName, ecRel: 'Parent',
                    ecDial: guardian.dial, ecPhone: guardian.phone,
                  }))
                }}
                style={{ ...prefillBtnStyle, marginBottom: 12 }}
              >{t('wizard.ec.useMe', { name: guardianName })}</button>
            )}
            {helpKey === 'ec' && (
              <div data-testid="wiz-helpbox" style={{ ...helpBoxStyle, margin: '0 0 12px' }}>
                {t('wizard.help.ec')}
              </div>
            )}
            <Gap h={6} />
            <label style={labelStyle}>{t('wizard.ec.name')}</label>
            <input data-testid="wiz-ec-name" value={draft.ecName} onChange={(e) => set('ecName', e.target.value)} style={inputStyle} />
            <Gap h={14} />
            <label style={labelStyle}>{t('wizard.ec.phone')}</label>
            <div style={phoneRowStyle}>
              <select data-testid="wiz-ec-dial" aria-label="Country code" value={ecDialValue} onChange={(e) => set('ecDial', e.target.value)} style={selectStyle}>
                {/* Blank placeholder (F2 belt-and-braces): an unmatched value
                    shows blank, never the alphabetically-first country. */}
                <option value="" />
                {dialOptions().map((o) => <option key={o}>{o}</option>)}
              </select>
              <input
                data-testid="wiz-ec-phone"
                value={draft.ecPhone}
                onChange={(e) => {
                  const f = filterPhoneInput(ecDialValue, e.target.value)
                  setEcStripped(f.stripped)
                  set('ecPhone', f.value)
                }}
                placeholder="50 987 6543"
                style={phoneBoxStyle(ecp.tone)}
              />
            </div>
            <div data-testid="wiz-ec-hint" style={phoneHintStyle(ecp.tone)}>
              {ecStripped ? t('wizard.phone.stripped') : ecp.hint}
            </div>
            <Gap h={16} />
            <label style={labelStyle}>{t('wizard.ec.rel')}</label>
            {/* Relationship set includes Child (README §Flows B). */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {RELATIONSHIPS.map((r) => (
                <button key={r} data-testid="wiz-ec-rel" data-value={r} onClick={() => set('ecRel', r)} style={pillStyle(draft.ecRel === r)}>{r}</button>
              ))}
            </div>
          </div>
        )}

        {/* STEP 5 — Preferences. Each question renders ONLY when its event
            toggle asks it (CMT-2026-08-29-022: an OFF flag never renders — no
            greyed-out filler); both off ⇒ the step itself dropped (clamped
            above), so this block can't render empty. */}
        {!held && step === 5 && (
          <div id="wizard.step5">
            {isChild ? (
              // A child inherits the account language — one field serves
              // interface + comms in V1; no second language question. The
              // notice keeps its child-only logic regardless of askLanguage
              // (it is information, not a question).
              <div data-testid="wiz-lang-inherited" style={{ fontSize: 11.5, color: 'var(--text-faint)', lineHeight: 1.5, marginBottom: 16 }}>
                {t('wizard.lang.inherited', { name: kid || who, lang: accountLang })}
              </div>
            ) : askLanguage ? (
              <>
                <label style={labelStyle}>{t('wizard.lang.label')}</label>
                <div style={noteStyle}>{t('wizard.lang.note')}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {LANGUAGES.map((l) => (
                    <button key={l} data-testid="wiz-lang" data-value={l} onClick={() => set('language', l)} style={pillStyle(draft.language === l)}>{l}</button>
                  ))}
                </div>
                <Gap h={20} />
              </>
            ) : null}
            {askShirt && (
              <>
                <label style={labelStyle}>{isChild ? t('wizard.shirt.junior') : t('wizard.shirt.self')}</label>
                {/* Age-suggested for children, always overridable (README §Flows B). */}
                {isChild && suggested && (
                  <div data-testid="wiz-shirt-suggest" style={noteStyle}>
                    {draft.shirt && draft.shirt !== suggested
                      ? t('wizard.shirt.overridden', { chosen: draft.shirt, age: age ?? '', suggested })
                      : t('wizard.shirt.suggested', { name: kid || who, age: age ?? '' })}
                  </div>
                )}
                <div data-testid="wiz-shirt-note" style={noteStyle}>{t('wizard.shirt.note')}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {(isChild ? JUNIOR_SHIRT_SIZES : SELF_SHIRT_SIZES).map((s) => (
                    <button key={s} data-testid="wiz-shirt" data-value={s} onClick={() => set('shirt', s)} style={{ ...pillStyle(effShirt === s), minWidth: 52, textAlign: 'center' }}>{s}</button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* DONE — the receipt. Heading derives from the DATA (allValid). */}
        {!held && isDone && (
          <div id="wizard.done">
            <div style={{ textAlign: 'center', padding: '6px 0 4px' }}>
              <div style={{ width: 58, height: 58, borderRadius: '50%', background: 'rgba(18,196,172,.16)', color: 'var(--accent-bright)', display: 'grid', placeItems: 'center', fontSize: 27, margin: '0 auto 14px' }}>
                {complete ? '✓' : '⋯'}
              </div>
              <h1 data-testid="wiz-done-heading" style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 6px' }}>
                {complete
                  ? isChild ? t('wizard.done.complete.child', { name: kid || who }) : t('wizard.done.complete.self')
                  : t('wizard.done.partial')}
              </h1>
              <p data-testid="wiz-done-sub" style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, margin: '0 0 16px' }}>
                {complete
                  ? isChild ? t('wizard.done.sub.complete.child', { name: kid || who }) : t('wizard.done.sub.complete.self')
                  : isChild ? t('wizard.done.sub.partial.child', { name: kid || who }) : t('wizard.done.sub.partial.self')}
              </p>
            </div>
            {!complete && (
              <div style={{ ...warnBoxStyle, marginTop: 0, marginBottom: 16 }}>
                <span style={{ flex: 'none', fontSize: 14, color: 'var(--warn)' }}>⚠</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div data-testid="wiz-done-missing" style={{ fontSize: 11.5, lineHeight: 1.5, color: 'var(--warn)', marginBottom: 9 }}>
                    {t('wizard.done.still', { list: missingList(draft, policy, t, identityLocked) })}
                  </div>
                  <button
                    data-testid="wiz-done-resume"
                    onClick={() => goStep(firstIncompleteStep(draft, policy, identityLocked))}
                    style={{ border: 0, borderRadius: 9, minHeight: 40, padding: '0 14px', background: 'var(--warn)', color: 'var(--warn-ink)', fontSize: 12.5, fontWeight: 800, fontFamily: 'inherit', cursor: 'pointer' }}
                  >{t('wizard.done.addNow')}</button>
                </div>
              </div>
            )}
            {/* The shared summary card (SummaryCard.tsx) — the SAME component
                the account edit hub renders with pencils (CMT-2026-09-03-026:
                one design, two homes). No pencils on the receipt. */}
            <SummaryCard rows={summaryRows(draft, gateCtx, t)} style={{ marginBottom: 20 }} />
          </div>
        )}
      </div>

      {/* BLOCKED-REASON BAR — directly above the footer button, the footer's
          own horizontal padding, naming the SPECIFIC missing fields in
          on-screen order (README blocked-reason contract; QA §9). */}
      {!held && !isDone && !ok && (
        <div id="wizard.block" data-testid="wiz-block" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: `10px ${blockPadX}px`, background: 'rgba(230,193,90,.1)', borderTop: '1px solid rgba(230,193,90,.28)' }}>
          <span style={{ flex: 'none', color: 'var(--warn)', fontSize: 13 }}>⚠</span>
          <span style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--warn)', fontWeight: 700 }}>{blockText(needs, t)}</span>
        </div>
      )}
      {/* ELIGIBILITY ADVISORY BAR (BOARDROOM-2026-08-28-194055 item 3;
          ADVISORY per PR #83 review P1) — the amber pattern carrying the plain
          per-axis copy when the draft clearly fails a held ticket's contract.
          The save is never blocked (a held seat can be a PROXY holding); the
          bar persists onto the SAVED receipt so the likely per-seat server
          reject is visible before it lands on the get-ready card. */}
      {eligBlock && (
        <div id="wizard.eligblock" data-testid="wiz-elig-block" role="alert" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: `10px ${blockPadX}px`, background: 'rgba(230,193,90,.1)', borderTop: '1px solid rgba(230,193,90,.28)' }}>
          <span aria-hidden style={{ flex: 'none', color: 'var(--warn)', fontSize: 13 }}>⚠</span>
          <span style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--warn)', fontWeight: 700 }}>{eligBlock}</span>
        </div>
      )}
      {saveError && (
        <div role="alert" style={{ flex: 'none', padding: `8px ${blockPadX}px`, fontSize: 11.5, fontWeight: 700, color: 'var(--error)' }}>
          {saveError}
        </div>
      )}

      {/* FOOTER — held EMPTY while an event config is in flight (P1-1,
          adversarial review), exactly like the body: an unheld footer computed
          Continue/Save against the DEFAULT total (5) and stayed CLICKABLE —
          a step-4 entry on a both-off event flashed `Continue`→`Save profile`
          on resolve, and a click during the hold navigated to a step that was
          about to be clamped away. The chrome (bar) stays; the buttons wait. */}
      <div id="wizard.foot" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 10, padding: footPad, borderTop: '1px solid var(--hairline-07)', background: 'var(--bg-frame)' }}>
        {held ? null : isDone ? (
          <button
            data-testid="wiz-done-close"
            onClick={exitFlow}
            style={{ width: '100%', border: 0, borderRadius: 12, minHeight: 48, background: 'var(--accent)', color: 'var(--accent-ink)', fontSize: 15, fontWeight: 800, fontFamily: 'inherit', cursor: 'pointer' }}
          >{isChild || hubEntry ? t('wizard.done.backToProfile') : t('wizard.done.backToGetReady')}</button>
        ) : (
          <>
            <button
              data-testid="wiz-back-btn"
              onClick={back}
              style={{ flex: 'none', border: '1px solid var(--hairline-14)', background: 'transparent', color: 'var(--text-softbtn)', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', minHeight: 48, borderRadius: 12, padding: '0 16px' }}
            >{t('flow.back')}</button>
            {/* HUB entry (CMT-2026-09-03-026, spec 3): the primary action is
                SAVE on EVERY step — a deep-linked single-field edit saves the
                whole (already-hydrated) draft and returns to the hub; it never
                marches onward through the flow. */}
            {step < total && !hubEntry ? (
              <button
                data-testid="wiz-continue"
                onClick={next}
                aria-disabled={!ok}
                style={{ flex: 1, border: 0, borderRadius: 12, minHeight: 48, background: 'var(--accent)', color: 'var(--accent-ink)', fontSize: 15, fontWeight: 800, fontFamily: 'inherit', ...(ok ? { cursor: 'pointer' } : { opacity: 0.35, cursor: 'not-allowed' }) }}
              >{t('flow.continue')}</button>
            ) : (
              <button
                data-testid="wiz-save"
                onClick={() => { void save() }}
                aria-disabled={!ok}
                style={{ flex: 1, border: 0, borderRadius: 12, minHeight: 48, background: 'var(--accent)', color: 'var(--accent-ink)', fontSize: 15, fontWeight: 800, fontFamily: 'inherit', ...(ok && saveState !== 'saving' ? { cursor: 'pointer' } : { opacity: 0.35, cursor: 'not-allowed' }) }}
              >{saveState === 'saving'
                  ? t('wizard.saving')
                  : isChild ? t('wizard.save.child', { name: kid || who }) : t('wizard.save.self')}</button>
            )}
          </>
        )}
      </div>
    </div>
  )
}
